// Settings → Master Menu and the item form: an item and its units. Each unit is its own
// product; the units of one item share a group (products.size_of_product_id,
// shared/productSizes.ts). Pure functions only.
import { groupProductSizes } from "@shared/productSizes";

type Unit = { id: number; sizeOfProductId?: number | null; caseFraction?: string | null; unitOrder?: number | null };

/**
 * Every unit of the item `product` is a unit of, itself included, in the order the
 * customer's card lists them (groupProductSizes): the order the admin dragged them into,
 * else the unit the others are linked to first, then the largest share of a case. Just
 * the product when it has no other units.
 */
export function unitsOfItem<P extends Unit>(product: P, products: P[]): P[] {
  const leadId = product.sizeOfProductId ?? product.id;
  const members = products.filter((p) => p.id === leadId || p.sizeOfProductId === leadId);
  if (!members.some((p) => p.id === product.id)) members.push(product);
  return groupProductSizes(members)[0]?.members ?? [product];
}

/**
 * A category's product ids for a reorder, and the ids that ride with the row above
 * them (lib/masterMenuFilter.ts reorderCategoryIds). The Master Menu draws an item with
 * several units as one row with its units under it, so the item moves as a block: its
 * units are listed straight after its first unit, and ride with it, as do the rows
 * "Hide inactive items" left out.
 */
export function categoryReorderList<P extends Unit>(
  categoryProducts: P[],
  hidden: ReadonlySet<number>,
): { ids: number[]; riders: Set<number> } {
  const ids: number[] = [];
  const riders = new Set<number>(hidden);
  for (const { members } of groupProductSizes(categoryProducts)) {
    members.forEach((member, index) => {
      ids.push(member.id);
      if (index > 0) riders.add(member.id);
    });
  }
  return { ids, riders };
}
